import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, CalendarDayButton } from "@/components/ui/calendar";
import { enumerateDateRange, formatDateOnly, parseDateOnly } from "@/utils/dateOnly";
import { cn } from "@/lib/utils";
import type { DayButton } from "react-day-picker";

const calendarClassName =
  "rounded-xl border border-white/10 bg-[#132035] text-white p-3 [--cell-size:2.4rem] select-none";

type SingleProps = {
  mode: "single";
  selected?: string | null;
  onSelect: (date: string | null) => void;
  allowedDates?: string[];
};

type MultipleProps = {
  mode: "multiple";
  selected: string[];
  onSelect: (dates: string[]) => void;
  fromDate?: Date;
  toDate?: Date;
};

type DragState = {
  start: string;
  current: string;
  additive: boolean;
  moved: boolean;
};

type DragContextValue = {
  onDayPointerDown: (key: string, event: React.PointerEvent) => void;
  previewSet: Set<string>;
  previewStart: string | null;
  previewEnd: string | null;
  suppressClickRef: React.MutableRefObject<boolean>;
};

const DragContext = createContext<DragContextValue | null>(null);

function dayKeyFromPoint(clientX: number, clientY: number): string | null {
  const target = document.elementFromPoint(clientX, clientY);
  const dayEl = target instanceof Element ? target.closest("[data-iso-day]") : null;
  return dayEl instanceof HTMLElement ? dayEl.dataset.isoDay ?? null : null;
}

function DragDayButton(props: React.ComponentProps<typeof DayButton>) {
  const drag = useContext(DragContext);
  const key = formatDateOnly(props.day.date);
  const inPreview = drag?.previewSet.has(key) ?? false;
  const isPreviewStart = inPreview && key === drag?.previewStart;
  const isPreviewEnd = inPreview && key === drag?.previewEnd;

  return (
    <CalendarDayButton
      {...props}
      data-iso-day={key}
      draggable={false}
      className={cn(
        props.className,
        "touch-none disabled:pointer-events-auto",
        inPreview &&
          "bg-[#29ffa9]/35 text-white data-[selected-single=true]:bg-[#29ffa9] data-[selected-single=true]:text-[#050d1a]",
        inPreview && isPreviewStart && "rounded-l-md",
        inPreview && isPreviewEnd && "rounded-r-md",
        inPreview && !isPreviewStart && !isPreviewEnd && "rounded-none",
      )}
      onDragStart={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        props.onPointerDown?.(event);
        drag?.onDayPointerDown(key, event);
      }}
      onClick={(event) => {
        if (drag?.suppressClickRef.current) {
          event.preventDefault();
          event.stopPropagation();
          drag.suppressClickRef.current = false;
          return;
        }
        props.onClick?.(event);
      }}
    />
  );
}

function CustomerCalendar({ selected, onSelect, allowedDates }: Omit<SingleProps, "mode">) {
  return (
    <Calendar
      mode="single"
      selected={selected ? parseDateOnly(selected) : undefined}
      onSelect={(date) => onSelect(date ? formatDateOnly(date) : null)}
      disabled={(date) => {
        const allowed = new Set(allowedDates ?? []);
        return allowed.size === 0 || !allowed.has(formatDateOnly(date));
      }}
      className={cn(calendarClassName)}
    />
  );
}

function InstallerCalendar({ selected, onSelect, fromDate, toDate }: Omit<MultipleProps, "mode">) {
  const today = useMemo(() => {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  }, []);
  const start = fromDate ?? today;
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedSetRef = useRef(selectedSet);
  selectedSetRef.current = selectedSet;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const isSelectable = useCallback(
    (key: string) => {
      const date = parseDateOnly(key);
      if (date < start) return false;
      if (toDate && date > toDate) return false;
      return true;
    },
    [start, toDate],
  );
  const isSelectableRef = useRef(isSelectable);
  isSelectableRef.current = isSelectable;

  const dragRef = useRef<DragState | null>(null);
  const unbindRef = useRef<(() => void) | null>(null);
  const suppressClickRef = useRef(false);
  const [preview, setPreview] = useState<{ start: string; current: string } | null>(null);

  const previewSet = useMemo(() => {
    if (!preview) return new Set<string>();
    return new Set(enumerateDateRange(preview.start, preview.current).filter(isSelectable));
  }, [preview, isSelectable]);
  const previewStart = preview
    ? (preview.start <= preview.current ? preview.start : preview.current)
    : null;
  const previewEnd = preview
    ? (preview.start <= preview.current ? preview.current : preview.start)
    : null;

  const finishDrag = useCallback(() => {
    unbindRef.current?.();
    unbindRef.current = null;

    const drag = dragRef.current;
    dragRef.current = null;
    setPreview(null);
    if (!drag?.moved) return;

    suppressClickRef.current = true;
    const days = enumerateDateRange(drag.start, drag.current).filter(isSelectableRef.current);
    if (days.length === 0) return;

    const next = new Set(selectedRef.current);
    if (drag.additive) {
      days.forEach((day) => next.add(day));
    } else {
      days.forEach((day) => next.delete(day));
    }
    onSelectRef.current([...next].sort());
  }, []);

  const updateDragCurrent = useCallback((key: string) => {
    const drag = dragRef.current;
    if (!drag || drag.current === key) return;
    if (key !== drag.start) drag.moved = true;
    drag.current = key;
    setPreview({ start: drag.start, current: key });
  }, []);

  useEffect(() => () => unbindRef.current?.(), []);

  const onDayPointerDown = useCallback(
    (key: string, event: React.PointerEvent) => {
      if (!isSelectable(key) || event.button !== 0) return;

      dragRef.current = {
        start: key,
        current: key,
        additive: !selectedSetRef.current.has(key),
        moved: false,
      };
      setPreview({ start: key, current: key });
      event.currentTarget.setPointerCapture?.(event.pointerId);

      const onMove = (moveEvent: PointerEvent) => {
        const nextKey = dayKeyFromPoint(moveEvent.clientX, moveEvent.clientY);
        if (nextKey) updateDragCurrent(nextKey);
      };
      const onUp = () => finishDrag();

      unbindRef.current?.();
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      unbindRef.current = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
    },
    [finishDrag, isSelectable, updateDragCurrent],
  );

  const dragContext = useMemo<DragContextValue>(
    () => ({
      onDayPointerDown,
      previewSet,
      previewStart,
      previewEnd,
      suppressClickRef,
    }),
    [onDayPointerDown, previewSet, previewStart, previewEnd],
  );

  return (
    <DragContext.Provider value={dragContext}>
      <Calendar
        mode="multiple"
        selected={selected.map(parseDateOnly)}
        onSelect={(dates) => onSelect((dates ?? []).map(formatDateOnly).sort())}
        disabled={{ before: start, ...(toDate ? { after: toDate } : {}) }}
        className={cn(calendarClassName, "touch-none")}
        components={{ DayButton: DragDayButton }}
      />
    </DragContext.Provider>
  );
}

export function InstallAvailabilityCalendar(props: SingleProps | MultipleProps) {
  if (props.mode === "single") {
    return (
      <CustomerCalendar
        selected={props.selected}
        onSelect={props.onSelect}
        allowedDates={props.allowedDates}
      />
    );
  }

  return (
    <InstallerCalendar
      selected={props.selected}
      onSelect={props.onSelect}
      fromDate={props.fromDate}
      toDate={props.toDate}
    />
  );
}
